import React, { useState } from 'react';
import { Avatar } from 'antd';
import {
    SpotifyPreviewCard,
    SpotifyHeader,
    SpotifyContent,
    ArtistImage,
    ArtistInfo,
    ProductCard,
    AnimatedSpotifyPlayer,
} from './styled';
import spotifyLogo from '../../../../../../../assets/images/spotify/spotify-logo.png';

const SpotifyCardComponent = ({ item, theme, onCardClick }) => {
    const [showPlayer, setShowPlayer] = useState(false);

    const cardClassName =
        theme?.card_style === 'fill-slightly-rounded'
            ? 'card-fill-slightly-rounded'
            : 'card-default';

    const handleClick = () => {
        // If displayOption === 'direct' OR it's a prerelease, open the link.
        if (item.displayOption === 'direct' || item.spotifyType === 'prerelease') {
            // If you have a separate field for the direct link, use that:
            // e.g. item.url or item.spotifyUrl
            window.open(item.url || item.spotifyUrl, '_blank');
            return;
        }

        setShowPlayer(!showPlayer);
        if (onCardClick) {
            onCardClick(item);
        }
        console.log('Card clicked:', item); // Debug log
    };

    // Helper: Render a Spotify iframe if displayOption === 'preview' or showPlayer is true
    const renderSpotifyEmbed = (height) => {

        // If displayOption === 'direct' or it's prerelease, skip the iframe
        if (item.displayOption === 'direct' || item.spotifyType === 'prerelease') {
            return null;
        }

        return (
            <iframe
                style={{ borderRadius: '12px', width: '100%', height }}
                src={`https://open.spotify.com/embed/${item.spotifyType}/${item.spotifyId}?utm_source=generator`}
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            />
        );
    };

    // Render the appropriate card layout with player below
    const renderCard = () => {
        // 1) BUTTON LAYOUT
        if (item.cardType === 'button') {
            return (
                <div>
                    <SpotifyPreviewCard
                        theme={theme}
                        className="button-layout"
                        onClick={handleClick}
                        role="button"
                        tabIndex={0}
                    >
                        <SpotifyContent layout="button" className="button-layout">
                            <ArtistImage
                                src={item.thumbnail || 'https://via.placeholder.com/150'}
                                alt={item.title}
                                className="button-image"
                            />
                            <ArtistInfo layout="button" theme={theme}>
                                <p className="button-name">{item.buttonName}</p>
                            </ArtistInfo>
                            <img
                                src={spotifyLogo}
                                style={{ width: '28px', height: '28px' }}
                                alt="Spotify"
                                className="spotify-logo"
                            />
                        </SpotifyContent>
                    </SpotifyPreviewCard>
                    <AnimatedSpotifyPlayer show={showPlayer} style={{ marginTop: '20px' }}>
                        {renderSpotifyEmbed('580px')}
                    </AnimatedSpotifyPlayer>
                </div>
            );
        }

        // 2) HIGHLIGHT LAYOUT
        if (item.cardType === 'highlight') {
            return (
                <div>
                    <SpotifyPreviewCard
                        theme={theme}
                        className="highlight-layout"
                        onClick={handleClick}
                        role="button"
                        tabIndex={0}
                    >
                        <SpotifyHeader theme={theme}>
                            <div />
                            <img src={spotifyLogo} alt="Spotify" className="spotify-logo" />
                        </SpotifyHeader>
                        <SpotifyContent layout="highlight" isArtist={item.spotifyType === 'artist'} className="highlight-layout">
                            <ArtistImage
                                src={item.thumbnail || 'https://via.placeholder.com/150'}
                                alt={item.title || item.artistName}
                            />
                            <ArtistInfo layout="highlight" theme={theme}>
                                <h3 className="artist-type">
                                    {item.spotifyType === 'album' || item.spotifyType === 'prerelease' ? item.albumName : item.artistName}
                                </h3>
                                <h2 className="artist-name">
                                    {item.spotifyType === 'album' || item.spotifyType === 'prerelease' ? item.artistName : 'Top Tracks'}
                                </h2>
                            </ArtistInfo>
                        </SpotifyContent>
                    </SpotifyPreviewCard>
                    <AnimatedSpotifyPlayer show={showPlayer} style={{ marginTop: '20px' }}>
                        {renderSpotifyEmbed('580px')}
                    </AnimatedSpotifyPlayer>
                </div>
            );
        }

        // 3) FALLBACK FOR OTHER CARD TYPES
        return (
            <div>
                <ProductCard
                    key={item._id}
                    theme={theme}
                    className={cardClassName}
                    onClick={handleClick}
                    role="button"
                    tabIndex={0}
                >
                    <div className="product-content">
                        <div className="product-image">
                            <Avatar
                                shape="square"
                                size={90}
                                src={item.thumbnail || 'https://via.placeholder.com/150'}
                            />
                        </div>
                        <div className="product-info">
                            <h3 style={{ color: theme?.card_text_color }}>{item.title}</h3>
                            {item.artistName && (
                                <p style={{ color: theme?.card_text_color }}>{item.artistName}</p>
                            )}
                        </div>
                    </div>
                    <div
                        className="product-button"
                        style={{
                            backgroundColor: theme?.button_color,
                            minHeight: '55px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0 20px',
                            borderRadius:
                                theme?.button_style?.includes('slightly-rounded')
                                    ? '10px'
                                    : theme?.button_style?.includes('squared')
                                        ? '0'
                                        : '15px',
                            color: theme?.button_text_color || '#FFFFFF',
                        }}
                    >
                        <span
                            className="button-text"
                            style={{ color: theme?.button_text_color }}
                        >
                            {item.buttonName || 'Open'}
                        </span>
                        <span className="button-icon">
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M5 12H19"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M12 5L19 12L12 19"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </div>
                </ProductCard>
                <AnimatedSpotifyPlayer show={showPlayer} style={{ marginTop: '20px' }}>
                    {renderSpotifyEmbed('580px')}
                </AnimatedSpotifyPlayer>
            </div>
        );
    };

    return renderCard();
};

export default SpotifyCardComponent;